<template>
  <d-modal
    :active="isActive"
    :width="600"
    @close="handleClose"
    :can-cancel="['escape', 'x', 'outside']"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="mb-6">
        <h3 class="text-xl font-semibold text-secondary-500">
          Duplicate Content Listing : {{ contentListing.name }}
        </h3>
        <div class="space-y-3 text-sm">
          <p>
            You can duplicate this content listing to the same subject area or to another one that you have access to. You can also duplicate it into the same subject area if you change the name. Note that resource types and categories assigned to a content listing will not be duplicated.
          </p>
          <p>
            Select the subject area below and make any adjustments to the name desired and then click duplicate to initiate the process. This may take a few minutes.
          </p>
          <p>
            Once duplication is complete, click out of this modal and navigate to/refresh the subject area to see the duplicated content. The content will be set as "not active" so that you can review it/make it active when you choose.
          </p>
        </div>
      </div>

      <!-- Duplication Form -->
      <form @submit.prevent="saveDuplication" class="space-y-4">
        <d-field label="Copy to Subject Area">
          <d-select v-model="subjectAreaId" required>
            <option v-for="subjectArea in subjectAreas" :key="subjectArea.name" :value="subjectArea.id">
              {{ subjectArea.name }}
            </option>
          </d-select>
        </d-field>
        
        <d-field label="New Name">
          <d-input v-model="newName" required />
        </d-field>

        <!-- Loading State -->
        <div v-if="saving" class="flex justify-center py-4">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-3 pt-4">
          <d-btn 
            type="button"
            flat 
            @click="handleClose"
          >
            Cancel
          </d-btn>
          <d-btn 
            type="submit"
            color="primary"
            :disabled="!isFormValid || saving"
            :loading="saving"
          >
            Duplicate
          </d-btn>
        </div>
      </form>
    </div>
  </d-modal>
</template>

<script>
import axios from 'axios';
import { showErrorToast } from '@services/errorHandling.service';

export default {
  name: 'DuplicateContentModal',
  
  props: {
    active: {
      type: Boolean,
      default: false
    },
    contentListing: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      subjectAreas: [],
      subjectAreaId: 0,
      newName: '',
      saving: false,
    };
  },

  computed: {
    isActive() {
      return this.active;
    },

    isFormValid() {
      return this.subjectAreaId > 0 && this.newName && this.newName.trim() !== '';
    }
  },

  watch: {
    active(newVal) {
      if (newVal) {
        this.resetForm();
        this.getSubjectAreas();
      }
    },

    contentListing: {
      handler(newVal) {
        if (newVal && newVal.name) {
          this.newName = newVal.name;
        }
      },
      immediate: true
    }
  },

  methods: {
    resetForm() {
      this.subjectAreaId = 0;
      this.newName = this.contentListing.name || '';
      this.saving = false;
    },

    async getSubjectAreas() {
      try {
        const params = ['currentPage=1', 'pageSize=1000'].join('&');
        const { data } = await axios.get(`/api/subject-areas/paged?${params}`);
        this.subjectAreas = data.resultItems;
      } catch (error) {
        showErrorToast(error);
      }
    },

    async saveDuplication() {
      if (!this.isFormValid) return;

      this.saving = true;
      try {
        await axios.post(`/api/content-listing/${this.contentListing.id}/duplicate?subjectAreaId=${this.subjectAreaId}&newName=${encodeURIComponent(this.newName)}`);
        this.$toast.success('Course was successfully duplicated');
        this.handleClose();
      } catch (error) {
        showErrorToast(error);
      } finally {
        this.saving = false;
      }
    },

    handleClose() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Remove any potential duplicate styling that might cause ghost outlines */
</style>